package co.maytes.standupbot.dashboard.pages

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.layout.MarketingShell
import co.maytes.standupbot.dashboard.components.marketing.Fold
import co.maytes.standupbot.dashboard.components.marketing.MkIcon
import co.maytes.standupbot.dashboard.components.marketing.PageIntro
import co.maytes.standupbot.dashboard.components.marketing.SectionHead
import co.maytes.standupbot.dashboard.components.motion.Reveal
import co.maytes.standupbot.dashboard.styles.Marketing
import co.maytes.standupbot.dashboard.styles.MarketingIcon
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.navigation.Link
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Page("/docs")
@Composable
fun DocsPage() {
    MarketingShell {
        Div(attrs = { classes(Marketing.PAGE_CLASS) }) {
            PageIntro(
                title = "Install, connect, post.",
                line = "Five steps from install to your first standup.",
            )
            StepsSection()
            DeepDivesSection()
        }
    }
}

private data class Step(
    val title: String,
    val line: String,
    val details: String,
)

private val steps: List<Step> = listOf(
    Step(
        title = "Install on Slack",
        line = "One click from the landing page.",
        details = "Click the button on the landing page. Slack will walk you through granting " +
            "`chat:write`, `commands`, and `channels:read`. We never ask for DM history.",
    ),
    Step(
        title = "Connect Linear",
        line = "One OAuth click. No API keys.",
        details = "Run the OAuth flow from the dashboard with one click and no API keys to copy. " +
            "StandupBot pulls assignees, issue transitions, and PR references from the teams " +
            "you pick, and nothing else.",
    ),
    Step(
        title = "Set up your standup",
        line = "Pick team, channel, days and time.",
        details = "Select the Linear team, choose the Slack channel to post to, and pick the days " +
            "and time in your team's timezone. The default is 9:30am on weekdays. You can skip " +
            "holidays later from the standup's Schedule tab.",
    ),
    Step(
        title = "Post your first standup",
        line = "Setup ends with a real one.",
        details = "The last step of setup generates a standup from the past 24 hours of Linear " +
            "activity and posts it to the channel you chose, so you see the real thing right away.",
    ),
    Step(
        title = "Add your Anthropic key",
        line = "Pro and Business run Claude on your account.",
        details = "Pro and Business workspaces run Claude on their own Anthropic account, while the Free " +
            "plan runs on ours. Create a key at console.anthropic.com, set a spend limit, and " +
            "paste it under Settings, then Integrations. The full walkthrough is linked below " +
            "under Creating your Anthropic key.",
    ),
)

@Composable
private fun StepsSection() {
    Section(attrs = { classes("mk-section", "mk-section--tight") }) {
        Div(attrs = { classes("mk-wrap", "mk-narrow") }) {
            Ol(attrs = { classes("mk-qsteps") }) {
                steps.forEachIndexed { index, step ->
                    Li(attrs = { classes("mk-qstep") }) {
                        Span(attrs = { classes("mk-qnum") }) { Text("${index + 1}") }
                        Reveal(index = index, modifier = Modifier.classNames("mk-qcard")) {
                            Div(attrs = { classes("mk-qcopy") }) {
                                H3(attrs = { classes("mk-dtitle") }) { Text(step.title) }
                                P(attrs = { classes("mk-body") }) { Text(step.line) }
                            }
                            Fold(title = "Details") { P(attrs = { classes("mk-body") }) { Text(step.details) } }
                        }
                    }
                }
            }
        }
    }
}

private data class DeepDive(
    val title: String,
    val line: String,
    val path: String,
    val icon: MarketingIcon,
)

private val deepDives: List<DeepDive> = listOf(
    DeepDive(
        title = "How synthesis works",
        line = "What Claude sees, and the fallback.",
        path = "/#pipeline",
        icon = MarketingIcon.History,
    ),
    DeepDive(
        title = "Creating your Anthropic key",
        line = "Add credit, create the key, set a spend limit.",
        path = "/docs/anthropic-key",
        icon = MarketingIcon.Key,
    ),
    DeepDive(
        title = "Security and privacy",
        line = "Encrypted tokens and minimum scope OAuth.",
        path = "/legal/privacy",
        icon = MarketingIcon.Shield,
    ),
)

@Composable
private fun DeepDivesSection() {
    Section(attrs = { classes("mk-section", "mk-band") }) {
        Div(attrs = { classes("mk-wrap") }) {
            SectionHead(title = "Past the basics.", line = "Three guides worth your time.")
            Div(attrs = { classes("mk-guides") }) {
                deepDives.forEachIndexed { index, dive ->
                    Reveal(index = index, modifier = Modifier.classNames("mk-guide")) {
                        MkIcon(dive.icon)
                        H3(attrs = { classes("mk-dtitle") }) { Text(dive.title) }
                        P(attrs = { classes("mk-body") }) { Text(dive.line) }
                        Link(
                            path = dive.path,
                            text = "Read more",
                            modifier = Modifier.classNames("mk-quiet"),
                        )
                    }
                }
            }
        }
    }
}
